import {
  FilterOnEmptyState,
  LiveFeedback,
  LoadingSpinner,
  NoFlyersAvailableEmptyState,
} from '@/components';
import { FlyerList } from '@/components/FlyerboardPagination/FlyerList';
import { JAR_TAG_FILTERS } from '@/context/JarFlyerboardProvider';
import { OwningTypeEnum } from '@/graphql/types';
import { useJarFlyerboardPagination, useLiveFeedback, useURLSearchParams } from '@/hooks';
import { css } from '@emotion/css';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export const EmbeddedJarList = () => {
  const { audienceId, districtId, audienceType, sortBy } = useURLSearchParams();
  const [liveMessage, setLiveMessage] = useLiveFeedback({ delay: 2500 });
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get('filter');
  const owningType =
    filterParam === JAR_TAG_FILTERS.SCHOOL
      ? OwningTypeEnum.district
      : filterParam === JAR_TAG_FILTERS.COMMUNITY
      ? OwningTypeEnum.organization
      : undefined;
  const isCommunityFree = filterParam === JAR_TAG_FILTERS.FREE ? true : undefined;
  const hasFilter = filterParam !== null && filterParam !== 'all';

  const { flyers, loading, fetchMoreFlyers, total } = useJarFlyerboardPagination({
    audienceId,
    districtId,
    flyerboardFilters: {
      schoolIds: audienceType === 'school' ? [Number(audienceId)] : [],
      owningType,
      isCommunityFree,
      allDistrictSchools: audienceType === 'district' ? true : undefined,
    },
    sortBy: sortBy ?? undefined,
    skipExpandedCommunityFlyers: true,
  });

  const wasLoadingRef = useRef(false);
  const prevFilterRef = useRef(filterParam);
  const announceResultTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      if (announceResultTimerRef.current) clearTimeout(announceResultTimerRef.current);
      // Delay lets NVDA finish processing the spinner removal and FlyerList mount
      // before the live region update arrives, preventing the announcement from being dropped.
      announceResultTimerRef.current = setTimeout(() => {
        setLiveMessage(flyers.length > 0 ? 'Flyers loaded.' : 'No flyers found.');
      }, 300);
    }
    wasLoadingRef.current = loading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // When Apollo serves cached data on a filter change, `loading` never flips to true,
  // so the effect above never fires. Watch filterParam directly as a fallback.
  useEffect(() => {
    const changed = filterParam !== prevFilterRef.current;
    prevFilterRef.current = filterParam;
    if (changed && !loading) {
      if (announceResultTimerRef.current) clearTimeout(announceResultTimerRef.current);
      // Longer delay so EmbeddedJarFilters' "X filter selected." announcement
      // reaches NVDA first before this one queues up.
      announceResultTimerRef.current = setTimeout(() => {
        setLiveMessage(flyers.length > 0 ? 'Flyers loaded.' : 'No flyers found.');
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParam]);

  useEffect(() => {
    return () => {
      if (announceResultTimerRef.current) clearTimeout(announceResultTimerRef.current);
    };
  }, []);

  const resetFilters = () => {
    searchParams.set('filter', 'all');
    setSearchParams(searchParams, { replace: true });
    setLiveMessage('Filters reset.');
  };

  const EmptyState = () => {
    if (flyers.length === 0 && !loading) {
      if (hasFilter) {
        return <FilterOnEmptyState onClick={resetFilters} />;
      }

      return <NoFlyersAvailableEmptyState />;
    }
    return null;
  };

  return (
    <main className={cn.container}>
      <LiveFeedback message={liveMessage} />
      {loading && flyers.length === 0 && <LoadingSpinner />}
      <EmptyState />
      {flyers.length > 0 && (
        <>
          <FlyerList
            flyers={flyers}
            nextPage={fetchMoreFlyers}
            total={total}
            trackId="jar"
            audienceId={audienceId}
            districtId={districtId}
            trackingEventFrom="jar"
            showTags
            isEmbedded={true}
          />
        </>
      )}
    </main>
  );
};

const cn = {
  container: css`
    flex: 1 1 auto;
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
    flex-grow: 10;
    padding-top: 24px;
  `,
};
